import { IdleIncluded } from '@evi/ui';
import { IdleReportCell } from '@/components/sessions/IdleReportCell';
import { DriverCell } from '@/components/sessions/DriverCell';
import { useCallback, useEffect, useState } from 'react';
import { formatReportMoney } from '@evi/shared';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  Activity, AlertTriangle, ChevronLeft, ChevronRight, Download, Loader2, StopCircle, Wifi, Zap,
} from 'lucide-react';
import {
  fetchSessions, forceCloseSession, formatCents,
  getDisplayErrorMessage, getErrorCode,
  type ForceCloseResult, type Session,
} from '@/lib/api';
import { useAuthStore } from '@/store/auth';
import { downloadAuthenticatedCsv } from '@/lib/downloadAuthenticatedCsv';
import { downloadErrorMessage } from '@/lib/downloadAuthed';
import { ResponsiveTable, type Column } from '@/components/ui/ResponsiveTable';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import { SESSION_PAYMENT_LABELS, sessionPaymentState, type SessionPaymentState } from '@/lib/sessionPayment';
import { sessionsSearchForView, sessionsViewFromSearch, type SessionsView } from '@/lib/sessionsViews';
import { livePowerText, recentMeterPower } from '@/lib/sessionPower';
import { useSessionClock } from '@/hooks/useSessionClock';
import { QUERY_CHANGE_EVENT } from '@/lib/chargerScope';
import styles from './SessionsPage.module.css';

interface SessionsPageProps {
  onNavigate: (path: string) => void;
}

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  sweeping: 'Pending',
  active: 'Active',
  completed: 'Completed',
  failed: 'Failed',
  stopped_by_user: 'Stopped (User)',
  stopped_by_system: 'Stopped (System)',
};

function formatDateTime(iso?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

function durationLabel(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

// ── Stalled-session eligibility (client mirror of the server guard) ──
// POST /admin/sessions/:id/force-close accepts: active AND (currently
// meter-silent >30 min OR flagged-and-PARKED by the reconciler). The
// sticky needs_force_stop flag alone is deliberately NOT enough — a
// session that went dark once and recovered stays flagged while
// charging healthily. The server re-checks on submit; this mirror only
// drives which rows show the badge and the button.
const STALL_SILENCE_MS = 30 * 60 * 1000;
const PARKED_PROBE_COUNT = 5; // = the reconciler's exported MAX_PROBES

function silenceMs(s: Session): number {
  const anchor = s.last_meter_value_at ?? s.started_at;
  return anchor ? Date.now() - new Date(anchor).getTime() : Number.POSITIVE_INFINITY;
}

function isStalled(s: Session): boolean {
  if (s.status !== 'active') return false;
  const parked =
    s.needs_force_stop === true && (s.reconcile_probe_count ?? 0) >= PARKED_PROBE_COUNT;
  return silenceMs(s) > STALL_SILENCE_MS || parked;
}

/** Paid only with money behind it; a start that never happened says
 *  "No charge", or "Released" when its hold was let go (lib/sessionPayment).
 *  A session still in flight renders the column's placeholder instead. */
function PaymentBadge({ state }: { state: Exclude<SessionPaymentState, 'live'> }) {
  // v1 history wears v3's colours for the same meaning: paid, still owed, neither.
  const cls = state === 'paid' || state === 'v1_paid' || state === 'v1_collected' ? styles.paidBadge
    : state === 'pending' || state === 'not_collected' || state === 'partly_paid' || state === 'failed' || state === 'v1_failed'
      || state === 'v1_pending' ? styles.pendingBadge
    : styles.noChargeBadge;
  return <span className={cls}>{SESSION_PAYMENT_LABELS[state]}</span>;
}

function silenceLabel(s: Session): string {
  const ms = silenceMs(s);
  if (!Number.isFinite(ms)) return 'no meter data received';
  return `silent ${durationLabel(ms / 60_000)}`;
}

/**
 * Admin sessions list.
 *
 * React-Query-backed with two live signals:
 *
 *   • Active sessions (live: session events refetch it, no poll):
 *     pinned at the top of the "All sessions" tab and the sole list
 *     on the "Active" tab. Live cells: power_kw + duration_minutes
 *     refresh with every meter sample, so operators watching a live
 *     charge see kW drift.
 *   • Paginated historical list (no poll) — keepPreviousData so page
 *     changes don't blank the table while the next page loads.
 *
 * The pinned-active group is suppressed when the status filter is
 * set to something other than 'active' — if you're specifically
 * looking at "completed" sessions, you don't want an active row
 * interrupting the view.
 *
 * No row-height jumps: the Power column is always present; non-
 * active rows render em-dash. Pinned rows live in a separate
 * table so their LIVE indicator + border treatment don't leak into
 * the historical list.
 */
export function SessionsPage({ onNavigate }: SessionsPageProps) {
  useRecentRoute('Sessions');
  // The tab rides ?tab=active (lib/sessionsViews.ts) so the dashboard's
  // Sessions widget opens the same view it shows (Warren, 28 Oct 2026).
  // replaceState, not pushState: flipping tabs adds no history entries.
  const [tab, setTabState] = useState<SessionsView>(() => sessionsViewFromSearch(window.location.search));
  const setTab = (next: SessionsView) => {
    setTabState(next);
    try {
      window.history.replaceState({}, '', `${window.location.pathname}${sessionsSearchForView(window.location.search, next)}`);
    } catch {
      /* sandboxed contexts without history: the tab still switches */
    }
  };
  const [page, setPage] = useState(1);
  // Back, forward, and a same-path move to the other tab while on this page
  // (/sessions to /sessions?tab=active renders no new route, so App.tsx
  // announces the query-only move with QUERY_CHANGE_EVENT), as the Chargers
  // page follows its ?scope=. Moving to All starts at page 1, as its tab does.
  useEffect(() => {
    const onUrl = () => {
      const next = sessionsViewFromSearch(window.location.search);
      if (next === tab) return;
      setTabState(next);
      if (next === 'all') setPage(1);
    };
    window.addEventListener('popstate', onUrl);
    window.addEventListener(QUERY_CHANGE_EVENT, onUrl);
    return () => {
      window.removeEventListener('popstate', onUrl);
      window.removeEventListener(QUERY_CHANGE_EVENT, onUrl);
    };
  }, [tab]);
  const [statusFilter, setStatusFilter] = useState('');
  const [forceTarget, setForceTarget] = useState<Session | null>(null);
  const queryClient = useQueryClient();
  const userRole = useAuthStore((s) => s.user?.role);
  // Cosmetic gate only — the route enforces the real rule (shared with
  // refund: super_admin, or tenant_admin scoped to the session tenant).
  const canForceClose = userRole === 'super_admin' || userRole === 'tenant_admin';
  // 'stalled' is a client-side view over the live active set: every
  // stalled session is active by definition, and the live active list
  // (limit 100) already carries the whole population.
  const stalledFilter = statusFilter === 'stalled';

  // Meta-carrying variant of the active poll (not fetchActiveSessions,
  // which discards meta.total): the server caps limit at 100 and sorts
  // started_at DESC, so past 100 concurrent actives the OLDEST rows —
  // exactly the likeliest-stalled ones — fall off this page. meta.total
  // lets us tell the operator the view is truncated instead of showing
  // a silently-incomplete ops queue.
  //
  // Live (28 Oct 2026): session events refetch both lists through
  // LiveUpdatesProvider; no poll. Stalling is the absence of events, so
  // the local clock below re-renders the stalled view without a fetch.
  const activeQuery = useQuery({
    queryKey: ['sessions', 'active'],
    queryFn: () => fetchSessions({ page: 1, limit: 100, status: 'active' }),
    staleTime: 5_000,
  });
  useSessionClock();

  const listQuery = useQuery({
    queryKey: ['sessions', 'list', { page, statusFilter }],
    queryFn: () =>
      fetchSessions({
        page, limit: 25,
        status: statusFilter || undefined,
      }),
    // The 'stalled' view renders from the active poll, not the
    // paginated list — 'stalled' is not a server status value.
    enabled: tab === 'all' && !stalledFilter,
    placeholderData: keepPreviousData,
  });

  const activeSessions = activeQuery.data?.data ?? [];
  const activeCount = activeSessions.length;
  const activeTotal = activeQuery.data?.meta.total ?? 0;
  const activeTruncated = activeTotal > activeSessions.length;
  const listData = listQuery.data?.data ?? [];
  const totalPages = listQuery.data?.meta.pages ?? 1;
  const total = listQuery.data?.meta.total ?? 0;
  // A disabled query (stalled view) retains its LAST error — don't let
  // a stale page-fetch failure banner over the independent stalled set.
  const relevantListError = stalledFilter ? null : listQuery.error;
  const error = (activeQuery.error ?? relevantListError) instanceof Error
    ? (activeQuery.error ?? relevantListError) as Error
    : null;

  const stalledSessions = activeSessions.filter(isStalled);

  // Pin active sessions to the top of the "All" tab unless the
  // operator has explicitly filtered to a non-active status.
  const showPinnedActive =
    tab === 'all'
    && !stalledFilter
    && activeCount > 0
    && (statusFilter === '' || statusFilter === 'active');

  // Drop pinned-active rows from the paginated list to avoid
  // duplicates. Keyed on _id since pagination + status filter can
  // both shift IDs across tabs.
  const activeIds = new Set(activeSessions.map((s) => s._id));
  const historical = showPinnedActive
    ? listData.filter((s) => !activeIds.has(s._id))
    : listData;

  const columns: Column<Session>[] = [
    {
      key: 'started',
      header: 'Started',
      mobile: 'primary',
      cell: (s) => (
        <span className={styles.dimText}>{formatDateTime(s.started_at)}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      mobile: 'secondary',
      cell: (s) => (
        <span className={styles.statusCell}>
          <span className={`${styles.statusBadge} ${styles[`status_${s.status}`] ?? ''}`}>
            {s.status === 'active' && <span className={styles.liveDot} aria-hidden="true" />}
            {STATUS_LABELS[s.status] ?? s.status}
          </span>
          {isStalled(s) && (
            <span className={styles.stalledBadge} title={silenceLabel(s)}>
              <AlertTriangle size={10} aria-hidden /> Stalled
            </span>
          )}
        </span>
      ),
    },
    // Who started it, failed starts included, linked to the driver profile
    // when the Api says it opens for this viewer (item 9, 2026-10-23).
    {
      key: 'driver',
      header: 'Driver',
      mobile: 'body',
      cell: (s) => <DriverCell row={s} onNavigate={onNavigate} />,
    },
    {
      key: 'energy',
      header: 'Energy',
      mobile: 'body',
      align: 'right',
      cell: (s) => `${s.energy_kwh.toFixed(2)} kWh`,
    },
    {
      key: 'power',
      header: 'Power',
      mobile: 'body',
      align: 'right',
      cell: (s) =>
        s.status === 'active'
          ? (
            <span className={styles.livePower}>
              <Zap size={11} />{livePowerText(recentMeterPower(s.meter_values, 1)[0])}
            </span>
          )
          : <span className={styles.dimText}>—</span>,
    },
    {
      key: 'duration',
      header: 'Duration',
      mobile: 'body',
      align: 'right',
      cell: (s) => durationLabel(s.duration_minutes),
    },
    {
      key: 'rfid',
      header: 'RFID',
      mobile: 'hidden',
      cell: (s) => <span className={styles.mono}>{s.rfid_tag ?? '—'}</span>,
    },
    {
      key: 'total',
      header: 'Total',
      mobile: 'body',
      align: 'right',
      cell: (s) => (
        <span className={styles.amount}>
          {formatReportMoney(s.cost?.total ?? 0, s.cost?.currency)}
          <IdleIncluded cents={s.idle_report?.idle_fee_cents ?? s.idle_evidence?.idle_fee_cents} currency={s.cost?.currency} locale={navigator.language} />
        </span>
      ),
    },
    {
      key: 'payment',
      header: 'Idle fee and collection',
      mobile: 'body',
      cell: (s) => <IdleReportCell report={s.idle_report} />,
    },
    {
      key: 'payment-status',
      header: 'Payment',
      mobile: 'body',
      cell: (s) => {
        const state = sessionPaymentState(s);
        return state === 'live' ? (
          <span className={styles.dimText}>—</span>
        ) : (
          <PaymentBadge state={state} />
        );
      },
    },
    {
      key: 'actions',
      header: '',
      mobile: 'body',
      align: 'right',
      cell: (s) =>
        canForceClose && isStalled(s) ? (
          <button
            type="button"
            className={styles.forceCloseBtn}
            onClick={(e) => {
              // Row click navigates to the detail page — keep the
              // action button from doing both.
              e.stopPropagation();
              setForceTarget(s);
            }}
          >
            <StopCircle size={12} aria-hidden /> Force close
          </button>
        ) : null,
    },
  ];

  const bothLoading =
    (tab === 'active' && activeQuery.isLoading)
    // Stalled view renders solely from the active poll — a disabled
    // listQuery reports isLoading:false, which would otherwise flash
    // the "no stalled sessions" all-clear before any data has loaded.
    || (tab === 'all' && stalledFilter && activeQuery.isLoading)
    || (tab === 'all' && !stalledFilter && listQuery.isLoading && activeQuery.isLoading);

  // CSV download — exports the full sessions list (no filter args
  // passed, preserving the prior <a href={exportSessionsCsvUrl()}>
  // behaviour). Routes through the authenticated ky client via
  // downloadAuthenticatedCsv; the previous <a download> pattern
  // 401'd because top-level browser navigation can't carry the
  // Bearer token. See admin-export-auth-cleanup ticket.
  // A failed export says why (29 Oct 2026), instead of failing silently.
  const [csvBusy, setCsvBusy] = useState(false);
  const [csvError, setCsvError] = useState<string | null>(null);
  const handleDownloadCsv = useCallback(async () => {
    if (csvBusy) return;
    setCsvBusy(true);
    setCsvError(null);
    try {
      const today = new Date().toISOString().slice(0, 10);
      await downloadAuthenticatedCsv({
        path: 'sessions/export',
        fallbackFilename: `evi-sessions-${today}.csv`,
      });
    } catch (err) {
      setCsvError(await downloadErrorMessage(err));
    } finally {
      setCsvBusy(false);
    }
  }, [csvBusy]);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.headerIcon}><Activity size={18} /></div>
          <div>
            <h1 className={styles.title}>Sessions</h1>
            <p className={styles.subtitle}>
              {tab === 'active'
                ? `${activeCount} active`
                : stalledFilter
                  ? `${stalledSessions.length} stalled`
                  : `${total} total`}
            </p>
          </div>
        </div>
        <button
          type="button"
          className={styles.csvBtn}
          onClick={handleDownloadCsv}
          disabled={csvBusy}
        >
          {csvBusy
            ? <Loader2 size={14} aria-hidden />
            : <Download size={14} />} Export CSV
        </button>
      </div>

      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${tab === 'all' ? styles.tabActive : ''}`}
          onClick={() => { setTab('all'); setPage(1); }}
        >
          All Sessions
        </button>
        <button
          className={`${styles.tab} ${tab === 'active' ? styles.tabActive : ''}`}
          onClick={() => setTab('active')}
        >
          <Wifi size={14} /> Active ({activeCount})
        </button>
      </div>

      {tab === 'all' && (
        <div className={styles.filterRow}>
          <select
            className={styles.select}
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          >
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="stalled">Stalled (needs force close)</option>
            <option value="pending">Pending</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
            <option value="stopped_by_user">Stopped by user</option>
            <option value="stopped_by_system">Stopped by system</option>
          </select>
        </div>
      )}

      {error && <div className={styles.error}>{error.message}</div>}
      {csvError && <div className={styles.error} role="alert">{csvError}</div>}

      {activeTruncated && (tab === 'active' || stalledFilter) && (
        <div className={styles.truncationNote}>
          Showing the {activeSessions.length} most recently started of {activeTotal} active
          sessions — older actives (the likeliest to be stalled) are not in this view.
        </div>
      )}

      {bothLoading ? (
        <p className={styles.loading}>Loading…</p>
      ) : tab === 'active' ? (
        <ResponsiveTable
          columns={columns}
          rows={activeSessions}
          rowKey={(s) => s._id}
          onRowClick={(s) => onNavigate(`/sessions/${s._id}`)}
          emptyMessage="No sessions currently active"
        />
      ) : stalledFilter ? (
        <ResponsiveTable
          columns={columns}
          rows={stalledSessions}
          rowKey={(s) => s._id}
          onRowClick={(s) => onNavigate(`/sessions/${s._id}`)}
          emptyMessage="No stalled sessions — nothing is waiting on a force close"
        />
      ) : (
        <>
          {showPinnedActive && (
            <section className={styles.pinnedSection}>
              <header className={styles.pinnedHeader}>
                <span className={styles.pinnedBadge}>
                  <span className={styles.liveDot} aria-hidden="true" />
                  LIVE
                </span>
                <span className={styles.pinnedLabel}>
                  {activeCount === 1
                    ? '1 active session'
                    : `${activeCount} active sessions`}
                </span>
              </header>
              <ResponsiveTable
                columns={columns}
                rows={activeSessions}
                rowKey={(s) => s._id}
                onRowClick={(s) => onNavigate(`/sessions/${s._id}`)}
                className={styles.pinnedTable}
              />
            </section>
          )}

          <ResponsiveTable
            columns={columns}
            rows={historical}
            rowKey={(s) => s._id}
            onRowClick={(s) => onNavigate(`/sessions/${s._id}`)}
            emptyMessage={
              showPinnedActive
                ? 'No historical sessions on this page.'
                : 'No sessions found'
            }
          />
        </>
      )}

      {tab === 'all' && !stalledFilter && totalPages > 1 && (
        <div className={styles.pagination}>
          <button
            className={styles.pageBtn}
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            <ChevronLeft size={16} /> Prev
          </button>
          <span className={styles.pageInfo}>Page {page} of {totalPages}</span>
          <button
            className={styles.pageBtn}
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next <ChevronRight size={16} />
          </button>
        </div>
      )}

      {forceTarget && (
        <ForceCloseModal
          session={forceTarget}
          onClose={(actioned) => {
            setForceTarget(null);
            // Refresh both the active poll and the paginated list —
            // the session either closed or settled through another
            // path; either way the row's status changed.
            if (actioned) void queryClient.invalidateQueries({ queryKey: ['sessions'] });
          }}
        />
      )}
    </div>
  );
}

// ── Force-close confirm dialog ───────────────────────────────────────
//
// Mirrors the RefundModal pattern from SessionDetailPage (the other
// admin money-confirm): fixed backdrop, stopPropagation card, inline
// styles. Three phases:
//   confirm — silence age + accrued cost, Cancel / Force close.
//   done    — the route's settle result (mode, collected, uncollected).
//   settled — 409 SESSION_ALREADY_SETTLED / 422 SESSION_NOT_ACTIVE:
//             a real stop (or a cron closer) won the race while the
//             admin was confirming. That is the GOOD outcome — show it
//             as such, never as an error.
const MODE_LABELS: Record<ForceCloseResult['mode'], string> = {
  wallet: 'Wallet — measured energy billed',
  payg_card: 'Pay-as-you-go: the hold settled for the measured cost, as a normal stop would',
  payg_card_pending: 'Pay-as-you-go: the card settlement could not finish; flagged, and the settlement sweep retries it',
  payg_card_dark: 'Pay-as-you-go — pre-auth released, cost recorded as uncollected',
  no_meter_data: 'No meter data — nothing billed, flagged for review',
  unbillable: 'Unbillable — flagged for review',
  fleet_billed: 'Fleet-billed — driver not charged, flagged for fleet settlement review',
};

function ForceCloseModal({
  session, onClose,
}: {
  session: Session;
  onClose: (actioned: boolean) => void;
}) {
  const [result, setResult] = useState<ForceCloseResult | null>(null);
  const [settledElsewhere, setSettledElsewhere] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => forceCloseSession(session._id),
    onSuccess: (r) => setResult(r),
    onError: async (err) => {
      const code = await getErrorCode(err);
      if (code === 'SESSION_ALREADY_SETTLED' || code === 'SESSION_NOT_ACTIVE') {
        setSettledElsewhere(true);
        return;
      }
      setSubmitError(
        await getDisplayErrorMessage(err, 'Force close failed — try again or check the audit log.'),
      );
    },
  });

  const currency = session.cost?.currency ?? 'USD';
  const accruedCents = session.cost?.total ?? 0;
  const finished = result !== null || settledElsewhere;
  const dismiss = () => {
    if (!mutation.isPending) onClose(finished);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="force-close-modal-title"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 16,
      }}
      onClick={dismiss}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(460px, 100%)',
          background: 'var(--color-surface, #1a1a1a)',
          border: '1px solid var(--color-border, var(--evi-border))',
          borderRadius: 8,
          padding: 20,
          boxShadow: '0 24px 64px rgba(0, 0, 0, 0.4)',
        }}
      >
        {settledElsewhere ? (
          <>
            <h2 id="force-close-modal-title" style={{ margin: '0 0 6px', fontSize: 16, fontWeight: 700 }}>
              Session already settled
            </h2>
            <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--color-tx-m)', lineHeight: 1.5 }}>
              This session was closed through another path — the charger sent its own
              stop, or the automated closer got there first — while you were confirming.
              Nothing left to do; the list will refresh.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button type="button" style={modalBtnStyle('primary')} onClick={() => onClose(true)}>
                OK
              </button>
            </div>
          </>
        ) : result ? (
          <>
            <h2 id="force-close-modal-title" style={{ margin: '0 0 6px', fontSize: 16, fontWeight: 700 }}>
              Session force-closed
            </h2>
            <div style={{ fontSize: 13, color: 'var(--color-tx-m)', lineHeight: 1.7, margin: '0 0 16px' }}>
              <div><strong>Billing:</strong> {MODE_LABELS[result.mode]}</div>
              <div><strong>Collected:</strong> {formatCents(result.collected_cents, currency)}</div>
              <div><strong>Uncollected:</strong> {formatCents(result.uncollected_cost_cents, currency)}</div>
              {result.manual_review_required && (
                <div style={{ marginTop: 6, color: 'var(--evi-warn, #f59e0b)' }}>
                  Flagged for manual review — see the session audit trail.
                </div>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button type="button" style={modalBtnStyle('primary')} onClick={() => onClose(true)}>
                Done
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 id="force-close-modal-title" style={{ margin: '0 0 6px', fontSize: 16, fontWeight: 700 }}>
              Force close this stalled session?
            </h2>
            {session.last_meter_value_at ? (
              <p style={{ margin: '0 0 10px', fontSize: 13, color: 'var(--color-tx-m)', lineHeight: 1.5 }}>
                Meter stream {silenceLabel(session)}. Accrued cost so far:{' '}
                <strong>{formatCents(accruedCents, currency)}</strong>.
              </p>
            ) : (
              <p style={{ margin: '0 0 10px', fontSize: 13, color: 'var(--color-tx-m)', lineHeight: 1.5 }}>
                No meter data was ever received for this session — closing will bill{' '}
                <strong>nothing</strong> and flag it for manual review.
              </p>
            )}
            <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--color-tx-m)', lineHeight: 1.5 }}>
              Bills only energy actually measured — wallet sessions collect now;
              pay-as-you-go pre-auths are released, with the cost recorded as
              uncollected for review. Anything delivered during the silence is not
              billed. This is recorded in the audit log under your account.
            </p>
            {submitError && (
              <div
                style={{
                  margin: '0 0 12px',
                  padding: '8px 10px',
                  fontSize: 12,
                  color: 'var(--evi-error, #ef4444)',
                  background: 'color-mix(in srgb, var(--evi-error, #ef4444) 10%, transparent)',
                  border: '1px solid color-mix(in srgb, var(--evi-error, #ef4444) 35%, transparent)',
                  borderRadius: 6,
                }}
              >
                {submitError}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                type="button"
                style={modalBtnStyle('ghost')}
                onClick={dismiss}
                disabled={mutation.isPending}
              >
                Cancel
              </button>
              <button
                type="button"
                style={modalBtnStyle('danger')}
                onClick={() => {
                  setSubmitError(null);
                  mutation.mutate();
                }}
                disabled={mutation.isPending}
              >
                {mutation.isPending ? 'Closing…' : 'Force close'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function modalBtnStyle(kind: 'primary' | 'danger' | 'ghost'): React.CSSProperties {
  const base: React.CSSProperties = {
    padding: '8px 14px',
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    border: '1px solid transparent',
  };
  if (kind === 'danger') {
    return {
      ...base,
      background: 'var(--evi-error, #ef4444)',
      border: '1px solid var(--evi-error, #ef4444)',
      color: '#fff',
    };
  }
  if (kind === 'primary') {
    return {
      ...base,
      background: 'var(--evi-primary, #22c55e)',
      border: '1px solid var(--evi-primary, #22c55e)',
      color: '#0a0a0a',
    };
  }
  return {
    ...base,
    background: 'transparent',
    border: '1px solid var(--color-border, var(--evi-border))',
    color: 'var(--color-tx)',
  };
}
